import React, { useEffect } from "react";
import moment from "moment";
import { Icon, Loader, Popup, Table } from "semantic-ui-react";

import { STATUS_COLORS } from "./constants";
import { useAuth } from "hooks";
import { multipleFhirGet } from "services";
import * as FHIRUtils from "../../../../utils/FHIRUtils";

type OrdersTableProps = {
  data: any;
  vendorView?: boolean;
};

const OrdersTable: React.FC<OrdersTableProps> = ({ data, vendorView }) => {
  const { token, organizationId } = useAuth();

  const isOrganizationPyx = organizationId === "PYX";

  const [patients, setPatients] = React.useState({});
  const [rawPatients, setRawPatients] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  const getPatients = async (urls) => {
    await multipleFhirGet(token, ...urls).then(
      (patientsDataset) => {
        //console.log(patientsDataset)
        let combinedPatients = [];
        let combinedRawPatients = [];
        patientsDataset.forEach((patientsData) => {
          if (patientsData && patientsData.entry && patientsData.entry.length) {
            let tempPatients = {};
            let tempRawPatients = {};
            patientsData.entry.forEach((item) => {
              tempPatients[item.resource.id] = FHIRUtils.parseFHIRPatient(item.resource);
              tempRawPatients[item.resource.id] = item.resource;
            });
            combinedPatients = { ...combinedPatients, ...tempPatients };
            combinedRawPatients = {
              ...combinedRawPatients,
              ...tempRawPatients,
            };
          }
        });
        setPatients(combinedPatients);
        setRawPatients(combinedRawPatients);
      },
      (error) => { }
    );
  };

  const fetchPatients = async () => {
    if (vendorView) {
      // get patient for each order
      if (data && data.length > 0) {
        let orgPatients = {}
        data
          .sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime())
          .forEach((order) => {
            //console.log(order.patientId, order.patientOrg)
            if (order.patientOrg) {
              if (orgPatients.hasOwnProperty(order.patientOrg)) {
                orgPatients[order.patientOrg].push(order.patientId);
              } else {
                orgPatients[order.patientOrg] = [order.patientId];
              }
            }
          });
        //console.log(orgPatients)
        let urls = [];
        if (Object.keys(orgPatients).length) {
          Object.keys(orgPatients).forEach((org) => {
            let patients = orgPatients[org];
            let uniquePatients = patients.filter((c, index) => {
              if (c && c !== '') return patients.indexOf(c) === index;
            });
            if (uniquePatients && uniquePatients.length) {
              const groups = uniquePatients
                .map((e, i) => {
                  return i % 50 === 0 ? uniquePatients.slice(i, i + 50) : null;
                })
                .filter((e) => {
                  return e;
                }); // creating chunks to fetch lesser patientIds in each call
              for (var groupIndex in groups) {
                urls.push(`/Patient/?organization=${org}&_id=${groups[groupIndex].join(',')}`);
              }
            }
          });
          //console.log(urls)
          await getPatients(urls);
        }
      }
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      await fetchPatients();
    };
    setLoading(true);
    fetchData().then(() => {
      setLoading(false);
    });
  }, [data]);

  return (
    <div className="table-div">
      <Table sortable celled singleLine>
        <Table.Header>
          {isOrganizationPyx ? (
            <Table.Row>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Order Placed</Table.HeaderCell>
              <Table.HeaderCell width={1}>Order Detail</Table.HeaderCell>
              <Table.HeaderCell>Carrier</Table.HeaderCell>
              <Table.HeaderCell>Tracking #</Table.HeaderCell>
              <Table.HeaderCell>Expected By</Table.HeaderCell>
              <Table.HeaderCell>Delivery Date</Table.HeaderCell>
            </Table.Row>
          ) : (
            <Table.Row>
              <Table.HeaderCell>Order ID</Table.HeaderCell>
              <Table.HeaderCell>Ordered</Table.HeaderCell>
              {vendorView && <Table.HeaderCell>Patient</Table.HeaderCell>}
              <Table.HeaderCell width={1}>Order Detail</Table.HeaderCell>
              <Table.HeaderCell width={1}>Flavor</Table.HeaderCell>
              <Table.HeaderCell width={1}>Qty.</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Expected By</Table.HeaderCell>
              <Table.HeaderCell>Carrier</Table.HeaderCell>
              <Table.HeaderCell>Tracking #</Table.HeaderCell>
              <Table.HeaderCell>Delivery Date</Table.HeaderCell>
            </Table.Row>
          )}
        </Table.Header>
        <Table.Body>
          {data.map((order) => {
            //console.log(order)
            //console.log(order.patientId)
            //console.log(patients[order.patientId])
            //console.log(patients[order.patientId]?.fullName)
            const orderStatus = order.status && order.status.toLowerCase();
            const trackingLink =
              order.deliveryInfo &&
              order.deliveryInfo.trackingCode &&
              order.deliveryInfo.carrier &&
              (order.deliveryInfo.carrier.toLowerCase() === "fedex"
                ? "https://www.fedex.com/apps/fedextrack/?tracknumbers=" + order.deliveryInfo.trackingCode
                : order.deliveryInfo.carrier.toLowerCase() === "ups"
                  ? "https://www.ups.com/track?tracknum=" + order.deliveryInfo.trackingCode
                  : "https://google.com/search?q=" +
                  order.deliveryInfo.trackingCode +
                  "+" +
                  order.deliveryInfo.carrier);
            let colorCode = STATUS_COLORS[orderStatus];
            let itemsContent = order.orderItems && order.orderItems.length > 0 && (
              <Table celled compact color="purple">
                <Table.Header>
                  <Table.Row textAlign="center">
                    <Table.HeaderCell>Food Items</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {order.orderItems.map((item, ind) => {
                    return (
                      <Table.Row key={ind}>
                        <Table.Cell>{item.name}</Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            );
            const orderName = (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>{isOrganizationPyx ? "Produce Box" : order.foodTypeName}</div>&nbsp;&nbsp;&nbsp;&nbsp;
                <div>
                  {itemsContent && (
                    <Popup
                      wide
                      on="click"
                      position="right center"
                      closeOnEscape
                      closeOnDocumentClick
                      content={itemsContent}
                      trigger={<Icon link name="clipboard list" color="purple" />}
                    />
                  )}
                </div>
              </div>
            );
            return (
              <Table.Row
                positive={colorCode === "yy"}
                warning={colorCode === "warning"}
                negative={colorCode === "negative"}
                error={colorCode === "error"}
                style={{
                  backgroundColor: (colorCode === "purple" && "#F0F8FF") || (colorCode === "purple" && "#ffefd5"),
                  color: colorCode === "zz" && "#808080"
                }}
              >
                {isOrganizationPyx ? (
                  <>
                    <Table.Cell>{order.status ? order.status.toString().toUpperCase() : ""}</Table.Cell>
                    <Table.Cell>{order.orderDate ? moment.utc(order.orderDate).local().fromNow() : ""}</Table.Cell>
                    <Table.Cell>{orderName}</Table.Cell>
                    <Table.Cell>{order.deliveryInfo ? order.deliveryInfo.carrier : ""}</Table.Cell>
                    <Table.Cell>
                      {order.deliveryInfo ? (
                        <a href={trackingLink} rel="noreferrer" target="_blank">
                          {order.deliveryInfo.trackingCode}
                        </a>
                      ) : (
                        ""
                      )}
                    </Table.Cell>
                    <Table.Cell>{order.estimatedDeliveryDate ? order.estimatedDeliveryDate : ""}</Table.Cell>
                    <Table.Cell>
                      {order.deliveryDate ? moment.utc(order.deliveryDate).local().format("YYYY-MM-DD") : ""}
                    </Table.Cell>
                  </>
                ) : (
                  <>
                    <Table.Cell>{order.orderId}</Table.Cell>
                    <Table.Cell>{order.orderDate ? moment.utc(order.orderDate).local().fromNow() : ""}</Table.Cell>
                    {vendorView &&
                      <Table.Cell>{
                        loading
                          ? <Loader active inline />
                          : patients[order.patientId]
                            ? patients[order.patientId].fullName
                            : ''
                      }</Table.Cell>}
                    <Table.Cell>{orderName}</Table.Cell>
                    <Table.Cell>{order.flavorName}</Table.Cell>
                    <Table.Cell>{order.quantity}</Table.Cell>
                    <Table.Cell>{order.status ? order.status.toString().toUpperCase() : ""}</Table.Cell>
                    <Table.Cell>{order.estimatedDeliveryDate ? order.estimatedDeliveryDate : ""}</Table.Cell>
                    <Table.Cell>{order.deliveryInfo ? order.deliveryInfo.carrier : ""}</Table.Cell>
                    <Table.Cell>
                      {order.deliveryInfo ? (
                        <a href={trackingLink} rel="noreferrer" target="_blank">
                          {order.deliveryInfo.trackingCode}
                        </a>
                      ) : (
                        ""
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {order.deliveryDate ? moment.utc(order.deliveryDate).local().format("YYYY-MM-DD") : ""}
                    </Table.Cell>
                  </>
                )}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};

export default OrdersTable;
